import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../ui/dialog';
import { Button } from '../../../../ui/button';
import { Input } from '../../../../ui/input';
import { Label } from '../../../../ui/label';
import { Textarea } from '../../../../ui/textarea';
import { Plus, Trash2, Pencil, Users } from 'lucide-react';
import { epicengageSupabase } from '../../../../../integrations/supabase/client';
import { useMerchant } from '../../../../../hooks/useMerchant';
import { toast } from 'sonner';

interface Segment {
  id: string;
  name: string;
  description: string | null;
  member_count?: number;
}

interface ManageSegmentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  segments: Segment[];
  onSegmentsChange: () => void;
}

export function ManageSegmentsDialog({ open, onOpenChange, segments, onSegmentsChange }: ManageSegmentsDialogProps) {
  const { merchant } = useMerchant();
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const handleCreate = async () => {
    if (!merchant || !newName.trim()) return;
    const { error } = await epicengageSupabase
      .from('segments')
      .insert({ merchant_id: merchant.id, name: newName.trim(), description: newDesc.trim() || null, rules: {} });
    if (error) {
      if (error.code === '23505') toast.error('Segment already exists');
      else toast.error(error.message);
      return;
    }
    setNewName('');
    setNewDesc('');
    onSegmentsChange();
  };

  const handleDelete = async (id: string) => {
    // Delete memberships first, then segment
    await epicengageSupabase.from('customer_segments').delete().eq('segment_id', id);
    const { error } = await epicengageSupabase.from('segments').delete().eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Segment deleted');
    onSegmentsChange();
  };

  const handleUpdate = async (id: string) => {
    if (!editName.trim()) return;
    const { error } = await epicengageSupabase
      .from('segments')
      .update({ name: editName.trim(), description: editDesc.trim() || null })
      .eq('id', id);
    if (error) {
      if (error.code === '23505') toast.error('Segment name already exists');
      else toast.error(error.message);
      return;
    }
    setEditingId(null);
    onSegmentsChange();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Segments</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Create */}
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Segment name"
              />
            </div>
            <div>
              <Label className="text-xs">Description (optional)</Label>
              <Textarea
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="Describe this segment"
                rows={2}
              />
            </div>
            <Button size="sm" onClick={handleCreate} disabled={!newName.trim()}>
              <Plus className="h-4 w-4 mr-1" /> Create Segment
            </Button>
          </div>

          {/* List */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {segments.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No segments yet</p>}
            {segments.map((seg) => (
              <div key={seg.id} className="p-3 rounded-lg border space-y-1">
                {editingId === seg.id ? (
                  <div className="space-y-2">
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8" />
                    <Textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={2} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleUpdate(seg.id)}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm">{seg.name}</p>
                      <div className="flex items-center gap-1">
                        {seg.member_count !== undefined && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Users className="h-3 w-3" />{seg.member_count}
                          </span>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => { setEditingId(seg.id); setEditName(seg.name); setEditDesc(seg.description || ''); }}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(seg.id)}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    {seg.description && <p className="text-xs text-muted-foreground">{seg.description}</p>}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
